import type { RequirementLevel } from "@nova-ai/shared"

import { cn } from "../../lib/utils"
import { Tooltip } from "../ui/tooltip"

// Requirement is a secondary signal, so it reads as a quiet dot + label rather
// than a loud uppercase pill: only `required` carries weight (it's the level a
// reviewer must act on), the rest fade toward invisible. The small leading dot
// keeps it scannable without competing with the label it sits beside.
export const REQUIREMENT_STYLES: Record<
  RequirementLevel,
  { dot: string; text: string; label: string }
> = {
  required: { dot: "bg-destructive", text: "text-destructive/80", label: "Required" },
  recommended: { dot: "bg-warning", text: "text-muted-foreground", label: "Recommended" },
  optional: { dot: "bg-muted-foreground/40", text: "text-muted-foreground/60", label: "Optional" },
  not_needed: {
    dot: "bg-muted-foreground/30",
    text: "text-muted-foreground/50",
    label: "Not needed",
  },
}

export function RequirementBadge({ requirement }: { requirement: RequirementLevel }) {
  const style = REQUIREMENT_STYLES[requirement]
  return (
    <span className={cn("inline-flex items-center gap-1 text-xs", style.text)}>
      <span className={cn("size-1 rounded-full", style.dot)} />

      {style.label}
    </span>
  )
}

/** Why a column reads "n/a" — shared by the badge tooltip and the drawer's
 *  "Not applicable" section header so both explain it the same way. */
export const OFF_CATEGORY_HINT =
  "Not an attribute of this product's category — it belongs to another category batched into this run."

/** Shown in the requirement slot for a feed column that is NOT an attribute of
 *  this SKU's category — it belongs to another category batched into the same
 *  run, so it is expected to be empty here. An outlined (not filled) dot marks
 *  it as "not applicable" rather than a requirement level. */
export function OffCategoryBadge() {
  return (
    <Tooltip content={OFF_CATEGORY_HINT}>
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/45">
        <span className="size-1 rounded-full border border-muted-foreground/50" />
        n/a
      </span>
    </Tooltip>
  )
}
